import { Injectable, Injector } from '@angular/core';
import { AngularFirestoreCollection } from '@angular/fire/firestore';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PeopleService } from 'src/pages/people/services/people/people.service';
import { Extender } from 'src/shared/helpers/extender';
import { Account, Feed } from 'src/shared/models';

@Injectable({
  providedIn: 'root'
})
export class FeedService extends Extender {

  public user: Account;

  constructor(
    protected injector: Injector,
    private peopleService: PeopleService
  ) {
    super(injector);
    this.auth$.user$.subscribe((user) => (this.user = user));
  }

  /**
   * get all users frends feed
   * @param uid
   */
  public getUserFriendsFeed(uid: any): Observable<any> {
    const reads$ = [];
    return this.peopleService.getFriendsIds(uid).pipe(
      switchMap((friendIds) => {
        if (friendIds.length > 0) {
          friendIds.map((id) => {
            reads$.push(this.getFriendFeedData(id));
          });
          return combineLatest(reads$);
        } else {
          return of([]);
        }
      }),
      map((feed: any) => {
        return feed.flat();
      })
    );
  }

  /**
   * get friends feed data or a collection of data based on limit info presented, defaults to 20
   * @param limit
   * @param id
   */
  public getFriendFeedData(limit = 20, id?): Observable<any> {
    let storeRef: AngularFirestoreCollection<Feed>;
    let feed: any[];

    if (id) {
      storeRef = this.afStore$.collection(`feed`, (ref) =>
        ref
          .where('uid', '==', id)
          .limit(limit)
          .orderBy('createdAt', 'desc')
      );
    } else {
      storeRef = this.afStore$.collection(`feed`, (ref) =>
        ref.limit(limit).orderBy('createdAt', 'desc')
      );
    }

    return storeRef.valueChanges().pipe(
      switchMap((post) => {
        if (post && post.length > 0) {
          const reads$ = [];
          feed = post;
          feed.forEach((_post) => {
            reads$.push(this.getFeedData(_post));
          });
          return combineLatest(reads$);
        } else {
          return [null]; }
      }),
      map((data) => {
        return data;
      })
    );
  }

  /**
   *
   * @param _feed get feeds comments, likes and user detils
   */
  public getFeedData(_feed): Observable<any> {
    let feed: any;
    return of(_feed).pipe(
      switchMap((post) => {
        const reads$ = [];
        feed = post;
        reads$.push(this.peopleService.getUser(feed.uid));
        reads$.push(this.getComments(feed.id));
        reads$.push(this.getLikes(feed));
        return combineLatest(reads$);
      }),
      map((data: any) => {
        return {
          ...feed,
          userLiked: !!data[2].find((like) => like.uid === this.user.uid),
          contact: data[0] || null,
          comments: data[1] || [],
          likes: data[2] || []
        };
      })
    );
  }

  /**
   * add or update feed
   * @param data
   */
  public addUpdateFeed(data: any) {
    if (!data || (data && !data.id)) { data.id = this.afStore$.createId(); }
    return this.afStore$
      .collection(`feed`)
      .doc(data.id)
      .set(data, { merge: true });
  }

  /**
   * delete feed
   * @param data
   */
  public deleteFeed(data: any) {
    return this.afStore$
      .collection(`feed`)
      .doc(data.id)
      .delete();
  }

  /**
   * get feed likes
   * @param feed
   */
  public getLikes(feed) {
    return this.afStore$
      .collection('feed')
      .doc(feed.id)
      .collection('likes')
      .valueChanges();
  }

  /**
   * like a feed by adding feed if userLiked property is false or removing like userLiked property is true
   * @param feed
   * @param uid
   */
  public likeFeed(feed: Feed, uid) {
    const ref = this.afStore$
      .collection('feed')
      .doc(feed.id)
      .collection('likes')
      .doc(uid);
    if (!feed.userLiked) {
      return ref.set({
        uid,
        createdAt: Date.now()
      });
    } else {
      return ref.delete();
    }
  }

  /**
   * get comments of a feed
   * @param fid
   */
  public getComments(fid) {
    const reads$ = [];
    let comments;
    return this.afStore$
      .collection('feed')
      .doc(fid)
      .collection('comments')
      .valueChanges()
      .pipe(
        switchMap((actions) => {
          if (actions.length === 0) {
            return of([]);
          } else {
            comments = actions.map((item: any) => {
              reads$.push(this.peopleService.getUser(item.uid));
              return item;
            });
            return combineLatest(reads$);
          }
        }),
        map((data) => {
          if (comments && comments.length > 0) {
            return comments.map((comment: any, i: string | number) => {
              return { ...comment, user: data[i] || null };
            });
          } else {
            return [];
          }
        })
      );
  }

  /**
   * send a comment
   * @param comment
   */
  public sendComment(comment) {
    comment.id = this.afStore$.createId();
    return this.afStore$
      .collection('feed')
      .doc(comment.fid)
      .collection('comments')
      .doc(comment.id)
      .set(comment);
  }

  /**
   * update comment
   * @param comment
   */
  public updateComment(comment) {
    return this.afStore$
      .collection('feed')
      .doc(comment.fid)
      .collection('comments')
      .doc(comment.id)
      .set(comment, { merge: true });
  }

  /**
   * remove comment
   * @param comment
   */
  public removeComment(comment) {
    return this.afStore$
      .collection('feed')
      .doc(comment.fid)
      .collection('comments')
      .doc(comment.id)
      .delete();
  }
}
